import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, TrendingUp, FileText, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube
} from 'lucide-react'
import { useProgressStore, PASS_SCORE } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'

// ────────────────────────────────────────────────────────────────────────────
// 상수
// ────────────────────────────────────────────────────────────────────────────
const UNIT_ID = 'F2-2'

// ────────────────────────────────────────────────────────────────────────────
// 시험 문항 타입
// ────────────────────────────────────────────────────────────────────────────
interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number          // 0-based index
  explanation: string[]   // 단계별 풀이
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

// ────────────────────────────────────────────────────────────────────────────
// 20문항 시험 데이터
// ────────────────────────────────────────────────────────────────────────────
const TEST_QUESTIONS: TestQuestion[] = [
  // ── 쉬움 (5문제) ─────────────────────────────────────────────────────────
  {
    id: 'FT01', number: 1, category: '방정식→직선 변환', difficulty: 'easy',
    question: '일차방정식 x + y − 3 = 0 을 y = ax + b 꼴로 나타내면?',
    choices: ['y = x + 3', 'y = −x + 3', 'y = x − 3', 'y = −x − 3'],
    answer: 1,
    explanation: [
      'x + y − 3 = 0 에서 y항을 분리합니다.',
      'y = −x + 3',
      '기울기 a = −1, y절편 b = 3',
    ],
    concept: '일차방정식·직선 변환', misconceptionCode: 'M01', learnStepId: 'FS01',
    misconception: '이항할 때 부호가 바뀝니다. x를 우변으로 이항하면 −x가 됩니다!',
  },
  {
    id: 'FT02', number: 2, category: '수직선·수평선', difficulty: 'easy',
    question: '방정식 y = 4 의 그래프는?',
    choices: ['기울기 4인 직선', 'x축에 평행한 수평선', 'y축에 평행한 수직선', '원점을 지나는 직선'],
    answer: 1,
    explanation: [
      'y = 4 는 y의 값이 항상 4인 점들의 집합입니다.',
      'x값에 상관없이 y = 4 → x축에 평행한 수평선',
      '기울기는 0입니다.',
    ],
    concept: '수평선·수직선', misconceptionCode: 'M02', learnStepId: 'FS01',
    misconception: 'y = k(상수)는 수평선, x = k는 수직선입니다. 혼동하지 마세요!',
  },
  {
    id: 'FT03', number: 3, category: '평행 조건', difficulty: 'easy',
    question: '두 직선 y = 2x + 1 과 y = 2x − 5 의 관계는?',
    choices: ['일치', '평행', '수직으로 교차', '한 점에서 교차'],
    answer: 1,
    explanation: [
      '두 직선의 기울기를 비교합니다: 2 = 2 (같음)',
      'y절편 비교: 1 ≠ −5 (다름)',
      '기울기가 같고 y절편이 다르면 → 평행',
    ],
    concept: '평행 조건', learnStepId: 'FS02',
  },
  {
    id: 'FT04', number: 4, category: '교점', difficulty: 'easy',
    question: '두 직선 y = x + 2 와 y = −x + 4 의 교점의 x좌표는?',
    choices: ['0', '1', '2', '3'],
    answer: 1,
    explanation: [
      '교점: 두 식을 같다고 놓고 풉니다.',
      'x + 2 = −x + 4',
      '2x = 2 → x = 1',
    ],
    concept: '교점·연립방정식', learnStepId: 'FS03',
  },
  {
    id: 'FT05', number: 5, category: '수직 조건', difficulty: 'easy',
    question: '기울기가 3인 직선과 수직인 직선의 기울기는?',
    choices: ['3', '−3', '1/3', '−1/3'],
    answer: 3,
    explanation: [
      '수직인 두 직선의 기울기 곱 = −1',
      '3 × m = −1',
      'm = −1/3',
    ],
    concept: '수직 조건', misconceptionCode: 'M03', learnStepId: 'FS02',
    misconception: '수직 조건: 기울기 곱 = −1. 역수에 부호를 바꿔야 합니다!',
  },

  // ── 보통 (7문제) ─────────────────────────────────────────────────────────
  {
    id: 'FT06', number: 6, category: '방정식→직선 변환', difficulty: 'medium',
    question: '2x − 3y + 6 = 0 을 y = ax + b 꼴로 변환할 때 기울기는?',
    choices: ['2/3', '−2/3', '3/2', '−3/2'],
    answer: 0,
    explanation: [
      '2x − 3y + 6 = 0 에서 y항을 분리',
      '−3y = −2x − 6',
      'y = (2/3)x + 2',
      '기울기 = 2/3',
    ],
    concept: '일차방정식·직선 변환', misconceptionCode: 'M01', learnStepId: 'FS01',
    misconception: '양변을 −3으로 나눌 때 부호에 주의하세요. −2x ÷ (−3) = 2/3 x',
  },
  {
    id: 'FT07', number: 7, category: '교점', difficulty: 'medium',
    question: '직선 y = 3x − 1 과 y = x + 5 의 교점의 좌표는?',
    choices: ['(3, 8)', '(2, 5)', '(3, 7)', '(2, 7)'],
    answer: 0,
    explanation: [
      '3x − 1 = x + 5',
      '2x = 6 → x = 3',
      'y = 3(3) − 1 = 8',
      '교점: (3, 8)',
    ],
    concept: '교점·연립방정식', learnStepId: 'FS03',
  },
  {
    id: 'FT08', number: 8, category: '연립방정식 그래프', difficulty: 'medium',
    question: '연립방정식 { x + y = 5, x − y = 1 } 의 해를 구하면?',
    choices: ['x=3, y=2', 'x=2, y=3', 'x=4, y=1', 'x=1, y=4'],
    answer: 0,
    explanation: [
      '두 식을 더합니다: 2x = 6 → x = 3',
      'x = 3을 첫 번째 식에 대입: 3 + y = 5 → y = 2',
      '해: (3, 2)',
    ],
    concept: '연립방정식 풀기', learnStepId: 'FS04',
  },
  {
    id: 'FT09', number: 9, category: '위치 관계', difficulty: 'medium',
    question: '두 직선 y = 2x + 3 과 y = 2x + 3 의 관계는?',
    choices: ['평행', '일치', '수직 교차', '한 점에서 교차'],
    answer: 1,
    explanation: [
      '두 직선의 기울기 비교: 2 = 2 (같음)',
      'y절편 비교: 3 = 3 (같음)',
      '기울기와 y절편이 모두 같으면 → 일치(같은 직선)',
    ],
    concept: '두 직선 위치 관계', misconceptionCode: 'M04', learnStepId: 'FS02',
    misconception: '기울기와 y절편이 모두 같으면 일치, y절편만 다르면 평행입니다!',
  },
  {
    id: 'FT10', number: 10, category: '그래프로 풀기', difficulty: 'medium',
    question: '두 직선 y = x + 1 과 y = −2x + 7 의 교점의 y좌표는?',
    choices: ['2', '3', '4', '5'],
    answer: 2,
    explanation: [
      'x + 1 = −2x + 7',
      '3x = 6 → x = 2',
      'y = 2 + 1 = 3? → y = (2) + 1 = 3',
      '다시 확인: y = −2(2) + 7 = 3. 교점 y좌표 = 3',
    ],
    concept: '교점·y좌표', learnStepId: 'FS03',
  },
  {
    id: 'FT11', number: 11, category: '연립방정식 해석', difficulty: 'medium',
    question: '연립방정식의 두 직선이 평행하면 해는?',
    choices: ['무수히 많다', '하나이다', '해가 없다', '두 개이다'],
    answer: 2,
    explanation: [
      '두 직선이 평행 → 교점이 없음',
      '교점 = 연립방정식의 해',
      '따라서 해가 없다 (불능)',
    ],
    concept: '연립방정식 해의 개수', misconceptionCode: 'M05', learnStepId: 'FS04',
    misconception: '평행 → 교점 없음 → 해 없음(불능). 일치 → 무수히 많음(부정). 혼동하지 마세요!',
  },
  {
    id: 'FT12', number: 12, category: '수직선 방정식', difficulty: 'medium',
    question: '점 (3, 0)과 (3, 5)를 지나는 직선의 방정식은?',
    choices: ['y = 3', 'x = 3', 'y = 3x', 'x = 5'],
    answer: 1,
    explanation: [
      '두 점의 x좌표가 모두 3으로 같습니다.',
      'x값이 항상 3 → x = 3인 수직선',
      '기울기는 정의되지 않습니다.',
    ],
    concept: '수직선', learnStepId: 'FS01',
  },

  // ── 어려움 (8문제) ────────────────────────────────────────────────────────
  {
    id: 'FT13', number: 13, category: '연립방정식 활용', difficulty: 'hard',
    question: '어른 입장료 a원, 어린이 입장료 c원일 때\n어른 2명+어린이 3명 = 13,000원\n어른 1명+어린이 2명 = 8,000원\n어른 입장료는?',
    choices: ['2,000원', '3,000원', '4,000원', '5,000원'],
    answer: 1,
    explanation: [
      '연립방정식: 2a + 3c = 13000 … ①, a + 2c = 8000 … ②',
      '② × 2: 2a + 4c = 16000 … ③',
      '③ − ①: c = 3000',
      'c=3000을 ②에 대입: a + 6000 = 8000 → a = 2000',
      '어른 입장료 = 2000원',
    ],
    concept: '연립방정식 활용', learnStepId: 'FS04',
  },
  {
    id: 'FT14', number: 14, category: '직선 방정식 결정', difficulty: 'hard',
    question: '두 직선 2x + y = 3 과 x − y = 0 의 교점을 지나고\n기울기가 2인 직선의 방정식은?',
    choices: ['y = 2x − 1', 'y = 2x + 1', 'y = 2x − 3', 'y = 2x + 3'],
    answer: 0,
    explanation: [
      '연립방정식으로 교점을 구합니다.',
      '2x + y = 3, x − y = 0 → 더하면 3x = 3, x = 1, y = 1',
      '교점: (1, 1)',
      '기울기 2, 점 (1, 1): y − 1 = 2(x − 1) → y = 2x − 1',
    ],
    concept: '직선 방정식·교점', learnStepId: 'FS03',
  },
  {
    id: 'FT15', number: 15, category: '연립방정식 해의 개수', difficulty: 'hard',
    question: '{ ax + 2y = 6, 3x + 6y = b } 가 해가 없으려면?',
    choices: ['a = 1, b = 18', 'a = 1, b ≠ 18', 'a ≠ 1, b = 18', 'a = 1, b = 6'],
    answer: 1,
    explanation: [
      '두 직선이 평행해야 해가 없습니다.',
      '두 직선의 기울기가 같고 y절편이 달라야 함',
      '①을 변환: y = −(a/2)x + 3, ②를 변환: y = −(1/2)x + b/6',
      '기울기 같으려면: a/2 = 1/2 → a = 1',
      'y절편 달라야: 3 ≠ b/6 → b ≠ 18',
    ],
    concept: '연립방정식 해의 개수', misconceptionCode: 'M05', learnStepId: 'FS04',
    misconception: '해가 없는 조건: 기울기 같고 y절편 다름. 일치 조건과 구별하세요!',
  },
  {
    id: 'FT16', number: 16, category: '그래프 면적', difficulty: 'hard',
    question: '두 직선 y = x 와 y = −x + 4, x축으로 둘러싸인 삼각형의 넓이는?',
    choices: ['2', '4', '8', '16'],
    answer: 1,
    explanation: [
      '세 꼭짓점을 구합니다.',
      '① y=x와 y=−x+4 교점: x=2, y=2 → (2, 2)',
      '② y=x와 x축(y=0) 교점: (0, 0)',
      '③ y=−x+4와 x축 교점: x=4, (4, 0)',
      '밑변 = 4, 높이 = 2',
      '넓이 = (1/2) × 4 × 2 = 4',
    ],
    concept: '그래프·면적', learnStepId: 'FS03',
  },
  {
    id: 'FT17', number: 17, category: '수직 교차', difficulty: 'hard',
    question: '직선 y = (1/2)x + 3 에 수직이고, 점 (2, 1)을 지나는 직선의 방정식은?',
    choices: ['y = 2x − 3', 'y = −2x + 5', 'y = 2x + 5', 'y = −2x − 3'],
    answer: 1,
    explanation: [
      '수직인 직선의 기울기: (1/2) × m = −1 → m = −2',
      '점 (2, 1)을 지남: y − 1 = −2(x − 2)',
      'y = −2x + 4 + 1 = −2x + 5',
    ],
    concept: '수직 조건·직선 방정식', misconceptionCode: 'M03', learnStepId: 'FS02',
  },
  {
    id: 'FT18', number: 18, category: '연립방정식 활용', difficulty: 'hard',
    question: '속력 문제: 올라갈 때 시속 3km, 내려올 때 시속 5km.\n총 이동 거리 16km, 총 시간은?',
    choices: ['3시간 12분', '3시간 44분', '4시간', '4시간 16분'],
    answer: 3,
    explanation: [
      '올라간 거리 = x km, 내려온 거리 = y km 로 놓으면',
      'x + y = 16 … ①',
      'x/3 + y/5 = 총 시간',
      'x = y = 8 (대칭) → 8/3 + 8/5 = 40/15 + 24/15 = 64/15 ≈ 4.27시간',
      '4시간 16분',
    ],
    concept: '연립방정식 실생활 활용', learnStepId: 'FS04',
  },
  {
    id: 'FT19', number: 19, category: '기울기·위치관계', difficulty: 'hard',
    question: '직선 3x − ky = 6 이 직선 y = 3x + 1 과 평행할 조건은?',
    choices: ['k = 1', 'k = −1', 'k = 3', 'k = −3'],
    answer: 0,
    explanation: [
      '3x − ky = 6 을 변환: y = (3/k)x − 6/k',
      'y = 3x + 1 의 기울기는 3',
      '평행 조건: 기울기 같음 → 3/k = 3 → k = 1',
      '단, y절편 −6/k = −6 ≠ 1 이므로 평행 확인 ✓',
    ],
    concept: '평행 조건·매개변수', misconceptionCode: 'M04', learnStepId: 'FS02',
  },
  {
    id: 'FT20', number: 20, category: '종합', difficulty: 'hard',
    question: '세 직선 x + y = 6, x − y = 2, y = k 가 삼각형을 이루지 못할 때 k의 값이 아닌 것은?',
    choices: ['k = 2', 'k = 4', 'k = 0', 'k = 6'],
    answer: 3,
    explanation: [
      '먼저 두 직선의 교점: x+y=6, x−y=2 → x=4, y=2. 교점 (4, 2)',
      '삼각형을 이루지 못하는 조건:',
      '① y=k가 두 직선 중 하나와 평행 또는 일치',
      '② y=k가 교점 (4,2)를 지남: k=2',
      '③ y=k가 x+y=6의 y절편(y=6, x=0)을 지남: k=6',
      '④ y=k가 x−y=2의 y절편(y=−2, x=0): k=−2',
      'k=4는 삼각형이 만들어지므로 답은 k=4가 아님. k=6일 때 불가.',
    ],
    concept: '종합·삼각형 조건', learnStepId: 'FS03',
  },
]

// ────────────────────────────────────────────────────────────────────────────
// 등급 설정
// ────────────────────────────────────────────────────────────────────────────
function getGrade(score: number): { grade: string; label: string; color: string; emoji: string; message: string } {
  if (score >= 95) return { grade: 'A+', label: '최우수', color: 'text-purple-700', emoji: '🏆', message: '완벽합니다! 일차함수와 방정식 마스터!' }
  if (score >= 90) return { grade: 'A',  label: '우수',   color: 'text-blue-700',   emoji: '🥇', message: '매우 훌륭합니다! 거의 완벽해요!' }
  if (score >= 80) return { grade: 'B',  label: '양호',   color: 'text-emerald-700',emoji: '🥈', message: '잘 했어요! 약간의 복습이 필요합니다.' }
  if (score >= 70) return { grade: 'C',  label: '보통',   color: 'text-yellow-700', emoji: '🥉', message: '기본은 됩니다. 오답 개념을 다시 확인해보세요.' }
  if (score >= 60) return { grade: 'D',  label: '미흡',   color: 'text-orange-700', emoji: '📚', message: '조금 더 노력이 필요합니다. 개념 학습부터 다시!' }
  return { grade: 'F', label: '부족', color: 'text-red-700', emoji: '💪', message: '포기하지 마세요! 처음부터 차근차근 다시 학습하세요.' }
}

const DIFF_COLOR = {
  easy:   'bg-emerald-100 text-emerald-700',
  medium: 'bg-blue-100 text-blue-700',
  hard:   'bg-red-100 text-red-700',
}
const DIFF_LABEL = { easy: '쉬움', medium: '보통', hard: '어려움' }

// 학습 단계 ID → 제목 매핑 (F2_2LearnPage STEPS와 일치)
const STEP_LABELS: Record<string, string> = {
  FS01: '일차방정식과 직선의 관계',
  FS02: '두 직선의 위치 관계',
  FS03: '두 직선의 교점',
  FS04: '그래프로 연립방정식 풀기',
  FS05: '연립방정식 해의 개수',
  FS06: '실생활 연립방정식 모델링',
  FS07: '연립방정식 활용 심화',
  FS08: '수직 조건과 직선 결정',
  FS09: '직선 방정식 종합',
  FS10: '오답 분석과 개념 정리',
}

// ────────────────────────────────────────────────────────────────────────────
// 시험 중 — 문항 표시 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
function QuestionView({ q, selected, onSelect }: {
  q: TestQuestion
  selected: number | null
  onSelect: (idx: number) => void
}) {
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-black">
          {q.number}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${DIFF_COLOR[q.difficulty]}`}>
          {DIFF_LABEL[q.difficulty]}
        </span>
        <span className="text-xs text-slate-400 font-mono">{q.category}</span>
      </div>
      <p className="text-sm font-semibold text-slate-800 leading-relaxed mb-4 whitespace-pre-line">
        {q.question}
      </p>
      <div className="space-y-2">
        {q.choices.map((choice, i) => (
          <button
            key={i}
            onClick={() => onSelect(i)}
            className={`w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
              selected === i
                ? 'border-indigo-500 bg-indigo-50 text-indigo-800 font-semibold'
                : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50 text-slate-700'
            }`}
          >
            <span className="font-bold mr-2 text-slate-400">{['①','②','③','④'][i]}</span>
            {choice}
          </button>
        ))}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 결과 화면 — 오답 풀이 카드
// ────────────────────────────────────────────────────────────────────────────
function WrongAnswerCard({ q, userAnswer, isOpen, onToggle, onGoLearn }: {
  q: TestQuestion
  userAnswer: number | null
  isOpen: boolean
  onToggle: () => void
  onGoLearn: (stepId: string) => void
}) {
  const [videoModal, setVideoModal] = useState(false)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const conceptKey     = q.learnStepId ?? q.concept
  const approvedVideos = getApprovedByTag(conceptKey)
  const hasApproved    = approvedVideos.length > 0
  const hasRecruiting  = !hasApproved && getRecruitingByTag(conceptKey).length > 0
  const isCorrect = userAnswer === q.answer
  const isSkipped = userAnswer === null

  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${
      isCorrect ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'
    }`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-white/50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black text-white flex-shrink-0 ${
          isCorrect ? 'bg-emerald-500' : 'bg-red-500'
        }`}>
          {q.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {isCorrect
              ? <span className="text-xs font-bold text-emerald-600 flex items-center gap-1"><CheckCircle size={11} /> 정답</span>
              : isSkipped
                ? <span className="text-xs font-bold text-slate-400">미응답</span>
                : <span className="text-xs font-bold text-red-600 flex items-center gap-1"><XCircle size={11} /> 오답</span>
            }
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${DIFF_COLOR[q.difficulty]}`}>
              {DIFF_LABEL[q.difficulty]}
            </span>
            <span className="text-xs text-slate-400">{q.category}</span>
          </div>
          <p className="text-xs text-slate-600 truncate">{q.question.split('\n')[0]}</p>
        </div>
        {!isCorrect && (
          isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" />
                 : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
        )}
      </button>

      {!isCorrect && isOpen && (
        <div className="border-t border-red-100 p-4 bg-white">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs font-bold text-red-500 mb-1">내 답 {isSkipped ? '(미응답)' : ''}</p>
              <p className="text-sm font-semibold text-red-700">
                {isSkipped ? '—' : `${['①','②','③','④'][userAnswer!]} ${q.choices[userAnswer!]}`}
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-500 mb-1">✅ 정답</p>
              <p className="text-sm font-semibold text-emerald-700">
                {['①','②','③','④'][q.answer]} {q.choices[q.answer]}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3">
            <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
              <PenTool size={11} /> 단계별 풀이
            </p>
            <div className="space-y-2">
              {q.explanation.map((line, i) => (
                <div key={i} className="flex gap-2">
                  <span className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold">
                    {i + 1}
                  </span>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{line}</p>
                </div>
              ))}
            </div>
          </div>

          {q.misconception && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">{q.misconception}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-400">연결 개념:</span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{q.concept}</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {hasApproved ? (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-gradient-to-r from-red-500 to-rose-500 text-white hover:opacity-90"
                  title={`승인된 영상 ${approvedVideos.length}개`}
                >
                  <Youtube size={11} />
                  🎬 영상으로 복습 ({approvedVideos.length})
                </button>
              ) : hasRecruiting ? (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-violet-100 text-violet-700 border border-violet-300 hover:bg-violet-200"
                >
                  <Youtube size={11} />
                  🎬 영상 모집 중
                </button>
              ) : (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-slate-100 text-slate-400 hover:bg-slate-200"
                >
                  <Youtube size={11} />
                  영상 복습
                </button>
              )}
              {q.learnStepId && (
                <button
                  onClick={() => onGoLearn(q.learnStepId!)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-indigo-100 text-indigo-700 rounded-xl font-bold hover:bg-indigo-200 transition-colors"
                >
                  <BookOpen size={11} />
                  {STEP_LABELS[q.learnStepId] ?? q.learnStepId} 개념 보기
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={q.learnStepId ?? q.concept}
          conceptLabel={q.concept}
          onClose={() => setVideoModal(false)}
          onCompleted={() => setVideoModal(false)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 메인 TEST 페이지
// ────────────────────────────────────────────────────────────────────────────
type Phase = 'intro' | 'test' | 'result'

export default function F2_2TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('intro')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(20).fill(null))
  const [currentQ, setCurrentQ] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [openReviewIds, setOpenReviewIds] = useState<Set<string>>(new Set())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions, init, getUnit } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
    init: s.init,
    getUnit: s.getUnit,
  }))

  useEffect(() => { init('student_001') }, [])

  const unitData = getUnit(UNIT_ID)
  const bestScore = unitData.bestScore
  const testCount = unitData.testRecords.length
  const isPassed  = unitData.passed

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const ss = s % 60
    return `${m.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`
  }

  const handleAnswer = (idx: number) => {
    const next = [...answers]
    next[currentQ] = idx
    setAnswers(next)
  }

  const submitTest = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)

    const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correctCount / 20) * 100)
    const gi = getGrade(sc)

    const wrongMCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    const wrongConcepts = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.concept)

    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount,
      totalCount: 20,
      durationSec: seconds,
      answers,
      wrongConceptIds: wrongConcepts,
      grade: gi.grade,
      passed: sc >= PASS_SCORE,
    })
    if (wrongMCodes.length > 0) {
      recordMisconceptions(UNIT_ID, wrongMCodes)
    }

    setPhase('result')
  }

  const resetTest = () => {
    setAnswers(Array(20).fill(null))
    setCurrentQ(0)
    setSeconds(0)
    setOpenReviewIds(new Set())
    setPhase('intro')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / 20) * 100)
  const gradeInfo = getGrade(score)
  const answeredCount = answers.filter(a => a !== null).length
  const skippedCount = 20 - answeredCount

  const toggleReview = (id: string) => {
    setOpenReviewIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const categoryStats = TEST_QUESTIONS.reduce((acc, q, i) => {
    if (!acc[q.category]) acc[q.category] = { total: 0, correct: 0 }
    acc[q.category].total++
    if (answers[i] === q.answer) acc[q.category].correct++
    return acc
  }, {} as Record<string, { total: number; correct: number }>)

  // ── 인트로 화면 ──────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Trophy size={40} className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-800 mb-2">실전 TEST</h1>
          <p className="text-slate-500">일차함수와 일차방정식 — 최종 실력 점검</p>

          {testCount > 0 && (
            <div className="mt-3 flex justify-center gap-3 flex-wrap">
              <span className={`text-sm px-3 py-1.5 rounded-full font-bold border-2 ${
                isPassed ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'
              }`}>
                {isPassed ? '✅ 합격 완료' : '⏳ 미합격'} | 최고점 {bestScore}점
              </span>
              <span className="text-sm px-3 py-1.5 rounded-full font-bold border-2 bg-indigo-50 border-indigo-200 text-indigo-700">
                {testCount}회 응시
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: <FileText size={20} />, label: '총 문항', value: '20문제', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
            { icon: <Clock size={20} />, label: '권장 시간', value: '25분', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { icon: <Zap size={20} />, label: '배점', value: '5점/문제', color: 'bg-orange-50 border-orange-200 text-orange-700' },
            { icon: <Award size={20} />, label: '합격 기준', value: `${PASS_SCORE}점 이상`, color: 'bg-purple-50 border-purple-200 text-purple-700' },
          ].map((item, i) => (
            <div key={i} className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${item.color}`}>
              <div className="opacity-70">{item.icon}</div>
              <div>
                <p className="text-xs font-semibold opacity-60">{item.label}</p>
                <p className="text-lg font-black">{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 mb-6">
          <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
            <BarChart2 size={16} className="text-indigo-500" /> 출제 영역
          </h3>
          <div className="space-y-2">
            {[
              { label: '쉬움 (easy)', count: 5, color: 'bg-emerald-400' },
              { label: '보통 (medium)', count: 7, color: 'bg-blue-400' },
              { label: '어려움 (hard)', count: 8, color: 'bg-red-400' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-32">{item.label}</span>
                <div className="flex-1 bg-slate-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${item.color}`}
                    style={{ width: `${(item.count / 20) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-600">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 mb-6">
          <h3 className="font-bold text-indigo-800 mb-2 flex items-center gap-2">
            <Brain size={15} /> 등급 기준
          </h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { grade:'A+', range:'95~100', color:'text-purple-700' },
              { grade:'A',  range:'90~94',  color:'text-blue-700' },
              { grade:'B',  range:'80~89',  color:'text-emerald-700' },
              { grade:'C',  range:'70~79',  color:'text-yellow-700' },
              { grade:'D',  range:'60~69',  color:'text-orange-700' },
              { grade:'F',  range:'0~59',   color:'text-red-700' },
            ].map(g => (
              <div key={g.grade} className={`bg-white rounded-lg py-1.5 border ${g.grade === 'C' || g.grade === 'B' || g.grade === 'A' || g.grade === 'A+' ? 'border-emerald-200' : 'border-slate-100'}`}>
                <p className={`text-lg font-black ${g.color}`}>{g.grade}</p>
                <p className="text-xs text-slate-400">{g.range}점</p>
                {g.range.startsWith('70') && (
                  <p className="text-xs text-emerald-600 font-bold">합격</p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-indigo-600 mt-2 text-center font-semibold">{PASS_SCORE}점 이상 = 단원 합격 🎯</p>
        </div>

        <button
          onClick={() => setPhase('test')}
          className="w-full py-4 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-2xl font-black text-lg hover:from-indigo-600 hover:to-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
        >
          <Zap size={20} /> {testCount > 0 ? `다시 시험 (${testCount + 1}회)` : '시험 시작'}
        </button>
        <p className="text-center text-xs text-slate-400 mt-3">시험을 시작하면 타이머가 작동합니다</p>
      </div>
    )
  }

  // ── 시험 중 화면 ─────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[currentQ]
    const progressPct = ((currentQ + 1) / 20) * 100

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 flex items-center gap-3 shadow-sm">
          <div className="flex-1">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>{currentQ + 1} / 20 문제</span>
              <span>{answeredCount} 문제 완료</span>
            </div>
            <div className="bg-slate-100 rounded-full h-2.5">
              <div
                className="bg-gradient-to-r from-indigo-500 to-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600 bg-slate-100 rounded-xl px-3 py-1.5">
            <Clock size={14} />
            <span className="font-mono text-sm font-bold">{formatTime(seconds)}</span>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap mb-4">
          {TEST_QUESTIONS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentQ(i)}
              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                i === currentQ
                  ? 'bg-indigo-500 text-white shadow-md'
                  : answers[i] !== null
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <QuestionView q={q} selected={answers[currentQ]} onSelect={handleAnswer} />

        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
            disabled={currentQ === 0}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all"
          >
            ← 이전
          </button>
          <div className="flex-1" />
          {currentQ < 19 ? (
            <button
              onClick={() => setCurrentQ(Math.min(19, currentQ + 1))}
              className="px-5 py-2.5 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition-all flex items-center gap-1.5"
            >
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={submitTest}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-black hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg flex items-center gap-1.5"
            >
              <Trophy size={14} /> 제출하기
            </button>
          )}
        </div>

        {currentQ < 19 && (
          <div className="mt-4 text-center">
            <button
              onClick={submitTest}
              className="text-xs text-slate-400 hover:text-slate-600 underline transition-colors"
            >
              {skippedCount > 0 ? `${skippedCount}문제 미응답 포함하여` : ''} 지금 제출하기
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── 결과 화면 ─────────────────────────────────────────────────────────────
  const wrongQuestions = TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer)
  const correctQuestions = TEST_QUESTIONS.filter((q, i) => answers[i] === q.answer)
  const passed = score >= PASS_SCORE

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-600 rounded-3xl p-6 text-white text-center mb-6 shadow-xl">
        <div className="text-5xl mb-2">{gradeInfo.emoji}</div>
        <div className={`text-7xl font-black mb-1`}>{gradeInfo.grade}</div>
        <p className="text-blue-100 text-sm mb-3">{gradeInfo.label}</p>
        <div className="text-5xl font-black mb-1">{score}점</div>
        <p className="text-blue-200 text-sm mb-2">{correctCount} / 20 문제 정답</p>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-black text-sm mb-3 ${
          passed ? 'bg-emerald-400/30 border-2 border-emerald-300' : 'bg-red-400/30 border-2 border-red-300'
        }`}>
          {passed ? '🎉 합격! 단원 완료' : `🎯 목표: ${PASS_SCORE}점 (${PASS_SCORE - score}점 더 필요)`}
        </div>
        <div className="bg-white/20 rounded-2xl p-3">
          <p className="text-white text-sm font-semibold">{gradeInfo.message}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-emerald-600 font-semibold mb-1">✅ 정답</p>
          <p className="text-3xl font-black text-emerald-700">{correctCount}</p>
          <p className="text-xs text-emerald-500">문제</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-red-600 font-semibold mb-1">❌ 오답</p>
          <p className="text-3xl font-black text-red-700">{wrongQuestions.length}</p>
          <p className="text-xs text-red-500">문제</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-slate-600 font-semibold mb-1">⏱ 소요</p>
          <p className="text-2xl font-black text-slate-700">{formatTime(seconds)}</p>
          <p className="text-xs text-slate-500">분:초</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
          <BarChart2 size={16} className="text-indigo-500" /> 영역별 분석
        </h3>
        <div className="space-y-2">
          {Object.entries(categoryStats).map(([cat, stat]) => {
            const pct = Math.round((stat.correct / stat.total) * 100)
            return (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-xs text-slate-600 w-28 font-medium truncate">{cat}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      pct >= 80 ? 'bg-emerald-400' : pct >= 60 ? 'bg-yellow-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-600 w-12 text-right">
                  {stat.correct}/{stat.total}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {wrongQuestions.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-500 rounded-xl flex items-center justify-center">
              <XCircle size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">오답 풀이</h3>
              <p className="text-xs text-slate-500">{wrongQuestions.length}문제 틀림 — 클릭하면 단계별 풀이 + 학습 이동</p>
            </div>
            <button
              onClick={() => setOpenReviewIds(new Set(wrongQuestions.map(q => q.id)))}
              className="ml-auto text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            >
              전체 펼치기
            </button>
          </div>
          <div className="space-y-3">
            {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map((q) => (
              <WrongAnswerCard
                key={q.id}
                q={q}
                userAnswer={answers[q.number - 1]}
                isOpen={openReviewIds.has(q.id)}
                onToggle={() => toggleReview(q.id)}
                onGoLearn={(stepId) => {
                  if (onGoLearn) onGoLearn(stepId)
                }}
              />
            ))}
          </div>
        </div>
      )}

      {correctQuestions.length > 0 && (
        <div className="mb-6">
          <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" /> 맞힌 문제 ({correctCount}문제)
          </h3>
          <div className="flex flex-wrap gap-2">
            {correctQuestions.map(q => (
              <span key={q.id} className="text-xs px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                ✓ {q.number}번
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-6">
        <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
          <Lightbulb size={16} /> 학습 추천
        </h3>
        <p className="text-sm text-amber-700 mb-3">
          {score >= 90
            ? '훌륭합니다! 이차함수 등 다음 단원으로 도전해보세요.'
            : score >= PASS_SCORE
              ? `합격! 틀린 ${wrongQuestions.length}문제의 개념을 다시 복습하고, 문제풀이 탭에서 유사 유형을 연습하세요.`
              : `아직 합격 점수(${PASS_SCORE}점)에 미달입니다. 오답 개념을 집중적으로 복습하세요.`
          }
        </p>
        {wrongQuestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[...new Set(wrongQuestions.map(q => q.concept))].map(concept => (
              <span key={concept} className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                📌 {concept}
              </span>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={resetTest}
        className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-2xl font-black text-base hover:from-indigo-600 hover:to-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
      >
        <RotateCcw size={18} /> 다시 시험 보기
      </button>
    </div>
  )
}
